import { Injectable, OnDestroy } from '@angular/core';
import { FormArray, FormControl } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { Select, Store } from '@ngxs/store';
import { Observable, Subject, filter, tap, takeUntil, merge, delay, take } from 'rxjs';

import { ActivityDateData, GetPlaceDetailsResponse, ImageApiService, ImageResultData } from '@hiptraveler/data-access/api';
import { AuthState } from '@hiptraveler/data-access/auth';
import { ItineraryState } from '@hiptraveler/data-access/itinerary';
import { SearchResultDialogActionService } from '@hiptraveler/dialogs/search-result-dialog';
import { ProfileStateFormService, ProfileStateService } from '../../../common';
import { CustomActivityDialogActionService } from '../../custom-activity-action-button/custom-activity-dialog';
import { SearchPageControlStateService } from '@hiptraveler/common';

@Injectable()
export class UploadsTabService implements OnDestroy {

  @Select(ItineraryState.actDate) actDate$: Observable<ActivityDateData[] | null>;

  subscription$ = new Subject<void>();

  constructor(
    private store: Store,
    public imageApi: ImageApiService,
    public searchResultDialog: SearchResultDialogActionService,
    public searchPageControl: SearchPageControlStateService,
    private stateService: ProfileStateService,
    private formService: ProfileStateFormService,
    private customActivityDialog: CustomActivityDialogActionService
    
  ) { }

  ngOnDestroy(): void {
    this.subscription$.next();
  }

  invalidStateObserver(): void {
    merge(
      this.stateService.selfProfile$.pipe( filter(e => !e) ),
      this.store.select(AuthState.authenticated).pipe( filter(e => !e) )
    ).pipe(
      delay(0),
      tap(() => this.stateService.profileTabSelection$$.next('about')),
      takeUntil(this.subscription$)
    ).subscribe();
  }

  openItinerary(result: ImageResultData): void {
    const ref = this.searchResultDialog.open(result, 'adventure');
    if (!ref) return;

    let imageResult: ImageResultData | null = null;
    let apiResponse: GetPlaceDetailsResponse | null = null;

    (ref as MatDialogRef<any>).afterOpened().subscribe(() => {
      this.store.select(ItineraryState.imageInformation).pipe(
        filter(Boolean)
      ).subscribe(response => {
        imageResult = response;
        this.imageApi.getPlaceDetails({ id: imageResult?.id || '' }).pipe(take(1)).subscribe((res) => {
          apiResponse = res;
        });
      });
    });

    (ref as MatDialogRef<any>).afterClosed().subscribe(response => {
      if (imageResult && response?.type === 'edit-upload') {
        this.formService.resetActivityDialogForm();

        const amenitiesFA = this.formService.activityDialogForm.get('amenities') as FormArray;
        const gygAmenitiesFA = this.formService.activityDialogForm.get('gygTour')?.get('amenities') as FormArray;
        const highlightsFA = this.formService.activityDialogForm.get('highlights') as FormArray;
        const inclusionsFA = this.formService.activityDialogForm.get('inclusions') as FormArray;
        const exclusionsFA = this.formService.activityDialogForm.get('exclusions') as FormArray;
        const tourMediaFA = this.formService.activityDialogForm.get('tourMedia') as FormArray;

        (apiResponse?.data?.content as any)?.imgTags?.forEach((value: any) => {
          if (!value) return;
          amenitiesFA.push(new FormControl(value));
          gygAmenitiesFA?.push(new FormControl(value));
        });
        (apiResponse?.data?.gygTour as any)?.highlights?.forEach((value: any) => {
          if (!value) return;
          highlightsFA.push(new FormControl(value));
        });
        (apiResponse?.data?.gygTour as any)?.inclusions?.forEach((value: any) => {
          if (!value) return;
          inclusionsFA.push(new FormControl(value));
        });
        (apiResponse?.data?.gygTour as any)?.exclusions?.forEach((value: any) => {
          if (!value) return;
          exclusionsFA.push(new FormControl(value));
        });
        (apiResponse?.data?.gygTour as any)?.tourMedia?.forEach((value: any) => {
          if (!value) return;
          tourMediaFA.push(new FormControl(value));
        });

        this.formService.activityDialogForm.patchValue({
          id: imageResult?.id,
          title: imageResult?.name,
          address: imageResult?.location,
          desc: imageResult.imgDesc,
          openingHours: (apiResponse?.data?.gygTour as any)?.openingHours,
          closingHours: (apiResponse?.data?.gygTour as any)?.closingHours,
          offer: (apiResponse?.data?.gygTour as any)?.tourDiscountOffer
        });
        this.formService.activityDialogForm.get('gygTour')?.patchValue({
          tourId: apiResponse?.data?.gygTour?.tourId
        });
        this.formService.activityDialogForm.get('gygTour')?.get('price')?.patchValue({
          amount: (apiResponse?.data?.gygTour as any)?.price?.amount
        });
        this.formService.activityDialogForm.get('gygTour')?.get('duration')?.patchValue({
          value: (apiResponse?.data?.gygTour as any)?.durationList?.[0]?.value,
          unit: (apiResponse?.data?.gygTour as any)?.durationList?.[0]?.unit
        });
        this.formService.activityDialogForm.get('cover')?.patchValue({
          imgUrl: imageResult?.imgUrl,
          imgThumbnail: imageResult?.thumbnail,
          hasCover: !!imageResult?.imgUrl
        });
        this.formService.activityDialogForm.get('locationMap')?.patchValue({
          city: imageResult?.address?.city,
          country: imageResult?.address?.country,
          id: imageResult?.address?.id
        });
        this.formService.activityDialogForm.get('otherInfo')?.patchValue({
          booking: imageResult.addInfo?.booking,
          website: imageResult.addInfo?.website,
          phone: imageResult.addInfo?.phone,
          email: imageResult.addInfo?.email,
          twitter: imageResult.addInfo?.twitter,
          facebook: imageResult.addInfo?.facebook,
          instagram: imageResult.addInfo?.instagram,
          whatsapp: imageResult.addInfo?.whatsapp,
          pinterest: imageResult.addInfo?.pinterest,
          wiki: imageResult.addInfo?.wiki,
          licence: imageResult.addInfo?.licence
        });
        this.stateService.resetScroll$.next(true);
        const ref = this.customActivityDialog.open(this.formService.activityDialogForm);
        (ref as MatDialogRef<any>).afterClosed().subscribe(() => {
          this.stateService.resetScroll$.next(false);
        });
      }
    });
  }

}
